import UpdateSheriffReportModal from "@/components/cases/update-sheriff-report-modal";
import FloatingAction from "@/components/core/actions/floating-action";
import Badge from "@/components/core/badge";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { FileLinkWithViewer } from "@/components/core/file-link-with-viewer";
import { useModal } from "@/components/core/modals/use-modal";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { userPermissions } from "@/constants/user-permissions";
import { caseActivitySheriffReports } from "@/features/case-evaluation/api/queries/acitivities-query";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { HasPermission } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { IconMoodLookDown, IconPencil, IconPlus, IconTerminal2 } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

const columns: ColumnDef<any>[] = [
	{
		id: "case_number",
		label: "Case No.",
		cellClassName: "",
		cell: ({ data, onClick }) => (
			<Link to={`/case-evaluation/${data.case.id}?status=${data.case.status}`} className="font-semibold text-blue-600 hover:text-blue-500">
				{data.case.case_number}
			</Link>
		),
	},
	{
		id: "case_title",
		label: "Case Title",
		cellClassName: "",
		cell: ({ data, onClick }) => {
			return data.case.case_title;
		},
	},
	{
		id: "hearing",
		label: "Hearing Schedule",
		cellClassName: "",
		cell: ({ data, onClick }) => {
			return data.hearingParent ? formatDate(data.hearingParent.scheduled_at) : "N/A";
		},
	},
	{
		id: "file",
		label: "File/s",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data, onClick }) => {
			return (
				<>
					{data.attachments.map((file, index) => (
						<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
					))}
				</>
			);
		},
	},
];

export const Route = createFileRoute("/_backoffice/sheriff-reports")({
	validateSearch: z.object({
		status: z.string().optional(),
		search: z.string().optional(),
		per_page: z.number().optional(),
		stage: z.string().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		status: search.status,
		stage: search.stage || "all",
		search: search.search,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseActivitySheriffReports({
				...deps,
			}),
		);
	},
	component: Page,
});

function Page() {
	const navigate = useNavigate({
		from: "/case-evaluation",
	});
	const search = Route.useSearch();
	const sheriffReportsQuery = useSuspenseQuery(
		caseActivitySheriffReports({
			...search,
			stage: search.stage || "all",
		}),
	);

	const sheriffReports = sheriffReportsQuery.data.data;
	const meta = sheriffReportsQuery.data.meta;
	const modal = useModal();

	console.log(sheriffReports);

	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStages = caseStageQuery.data.data;
	return (
		<Content pageName="Cases">
			{/*<Header title="Cases" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full gap-4 mt-4">
						{sheriffReports.length > 0 ? (
							<>
								<DataTable
									className="border-collapse"
									data={sheriffReports}
									columns={columns}
									stickyHead={false}
									stickyCol={false}
									showBorder={true}
									compact={true}
									showToolbar={true}
									options={(row) => (
										<>
											<DropdownMenuItem
												onClick={() => {
													console.log(row, "asdas");
													const tsnModal = modal.openModal({
														title: "Update Sheriff's Report",
														size: "lg",
														component: <UpdateSheriffReportModal data={row} />,
													});
												}}
											>
												<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
													<IconPencil size={16} stroke={2} />
													<span>Update Sheriff's Report</span>
												</div>
											</DropdownMenuItem>
										</>
									)}
								/>
								<Pagination
									onClick={async (page) => {
										await navigate({
											to: "/case-evaluation",
											search: (old) => ({
												...old,
												page,
											}),
										});
									}}
									initialPage={meta.current_page}
									pageCount={meta.last_page}
								/>
							</>
						) : (
							<Blanket width="sm">
								<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
								<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
							</Blanket>
						)}
					</div>
				</div>
			</div>
		</Content>
	);
}
